import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Delete } from '@mui/icons-material';
import { alpha, Chip, InputLabel, MenuItem, Typography, Checkbox, FormControlLabel, Stack } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { omit, uniq } from 'lodash-es';
import { Controller, SubmitErrorHandler, useForm, useWatch } from 'react-hook-form';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import {
  ContentCollectionResponse,
  ContentCollectionSortingType,
  ContentCollectionType,
  DocumentLocale,
  EntitlementType,
  PermissionsGroupId,
  Top10CountryInfoResponse,
  VersionedDocumentStatus
} from '../../../API';
import { useData } from '../../../data-layer';
import { useCollectionDisplay, useLocales, useNotifications, useTheme } from '../../../hooks';
import {
  collectionTypeCaptions,
  sortingTypeCaptions,
  sortingTypeDescriptions
} from '../../../utils/consts/translationKeys';
import { CustomValidators } from '../../../utils/customValidators';
import Button from '../../shared/Button';
import Code from '../../shared/Code';
import DraftBadge from '../../shared/DraftBadge';
import Drawer from '../../shared/Drawer';
import FormControl from '../../shared/FormControl';
import InputController from '../../shared/InputController';
import LocalizedInputCollection from '../../shared/LocalizedInputCollection';
import Localized from '../../shared/Localized';
import useConfirm from '../../../hooks/General/useConfirm';
import { EntitlementsMultiSelect } from '../../shared/Entitlements';
import { LocaleKeys } from '../../../locales/i18n';
import { usePermissions } from '../../../hooks/Permissions/usePermissions';
import { CollectionDeleteConfirm } from '../CollectionDeleteConfirm';
import AssetBrowser from '../../Assets/AssetBrowser';
import { AssetTypes } from '../../../utils/assetTypes';
import { Slug } from '../../shared/Slug';
import { handleSaveDraft, handleSavePublish, isFormValid } from '../../../utils/formHelpers';
import { defaultAutoRotateParams } from '../../../utils/autoRotate';
import TextValidator from '../../shared/TextValidator';
import Select from '../../shared/Select';
import { usePermissionsGuard } from '../../../hooks/General/usePermissionsGuard';
import ClearableDateTimePicker from '../../shared/ClearableDateTimePicker';
import { renderFormLabel } from '../../../utils/formUtils';
import { DateTime } from 'luxon';
import { HomepageOptions } from '../../../state/theme';
import { collectionFormTestIds } from '../../shared/TestsIds';
import CountryPickerLite from '../../shared/CountryPickerLite';
import PermissionsGroupsSelector from '../../shared/PermissionsGroupsSelector';
import {
  buildPublishedTiersByCountry,
  normalizeTop10CountryInfoRow
} from '../../../utils/top10PublishedTiersByCountry';

const useStyles = makeStyles()((theme) => ({
  headerLeft: {
    display: 'flex',
    gap: theme.spacing(4),
    alignItems: 'center'
  },
  sortingMenuItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start'
  },
  sortingDescription: {
    whiteSpace: 'normal',
    color: alpha(theme.palette.text.primary, 0.6),
    fontSize: theme.typography.body2.fontSize
  },
  formBody: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(4),
    gap: theme.spacing(4)
  },
  formField: {
    maxWidth: 500,
    marginBottom: 4
  },
  licContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    '& > div': {
      flexGrow: 1
    }
  },
  footerButton: {
    minWidth: 190,
    marginRight: theme.spacing(4)
  },
  selectControl: {
    minWidth: 500,
    marginBottom: theme.spacing(4)
  },
  selectOption: {
    textTransform: 'capitalize'
  },
  metaGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: theme.spacing(4)
  },
  startDate: {
    display: 'inline-flex',
    flexDirection: 'column'
  }
}));

function CollectionForm(): JSX.Element {
  const { canDelete, canPublish, canSave } = usePermissionsGuard({
    homepageOption: HomepageOptions.COLLECTIONS
  });
  const formRef = useRef<ValidatorForm>(null);
  const { classes } = useStyles();
  const { t } = useLocales();
  const { formControlColor } = useTheme();
  const { notifyError } = useNotifications();
  const { hasPermissions } = usePermissions();
  const navigate = useNavigate();

  const {
    collections: {
      state: collectionsState,
      hook: { remove, closeForm, saveAndPublish, save, publish, getTop10CountriesInfo }
    }
  } = useData();

  const genresList = useRecoilValue(collectionsState.withAllGenres);
  const formMetadata = useRecoilValue(collectionsState.withFormMetadata);
  const isSaving = useRecoilValue(collectionsState.withIsSaving);
  const isPublishing = useRecoilValue(collectionsState.withIsPublishing);
  const collection = formMetadata.record;
  const collectionEntityId =
    collection && 'entityId' in collection ? (collection as ContentCollectionResponse).entityId : undefined;
  const cloningCollectionTitle = formMetadata.cloningRecord?.title;
  const [isForSimilarsDisabled, setIsForSimilarsDisabled] = useState(!collection?.genre?.key);
  const {
    isVOD,
    isMixed,
    isTop10,
    getContentCollectionType,
    getAppRoute,
    getCollectionFormCopy,
    getContentCollectionTypes
  } = useCollectionDisplay();
  const [availableTop10Countries, setAvailableTop10Countries] = useState<string[]>([]);
  const [top10CountriesInfo, setTop10CountriesInfo] = useState<
    Array<Top10CountryInfoResponse & { entitlements: Array<string | EntitlementType> }>
  >([]);
  const top10CountriesFetchGenerationRef = useRef(0);
  const getTop10CountriesInfoRef = useRef(getTop10CountriesInfo);
  getTop10CountriesInfoRef.current = getTop10CountriesInfo;
  const collectionTypes = getContentCollectionTypes().filter(
    (key) => !['SPORTS_EVENTS', 'SPORTS'].includes(key)
  ) as Array<keyof typeof ContentCollectionType>;

  const { handleSubmit, control, reset, getValues, setValue, watch } = useForm<ContentCollectionResponse>({
    defaultValues: collection
  });
  const { confirm } = useConfirm();
  const allPermissionsGroupIds = Object.values(PermissionsGroupId);
  const {
    UPSERT: hasUpsertPermission,
    PUBLISH: hasPublishPermission,
    DELETE: hasDeletePermission
  } = hasPermissions(formMetadata.isNew ? allPermissionsGroupIds : collection?.ownerPermissionsGroups);

  const title = useWatch({ control, name: 'title' });
  const isAutoRotateEnabled = !!useWatch({ control, name: 'autoRotate.numberOfItems' });
  const selectedEntitlements = useWatch({ control, name: 'entitlements' });

  const [splashLandscapeImage, splashPortraitImage] = watch(['splashLandscapeImage', 'splashPortraitImage']);

  const publishedTiersByCountry = useMemo(
    () => buildPublishedTiersByCountry(top10CountriesInfo, collectionEntityId),
    [top10CountriesInfo, collectionEntityId]
  );

  const initTop10CountriesFields = useCallback(() => {
    if (!isTop10) return;

    const requestGeneration = ++top10CountriesFetchGenerationRef.current;

    const fetchTop10CountriesInfo = async () => {
      const countriesInfo = await getTop10CountriesInfoRef.current();

      if (requestGeneration !== top10CountriesFetchGenerationRef.current) {
        return;
      }

      const normalizedRows = countriesInfo.map((country) =>
        normalizeTop10CountryInfoRow(
          country as Top10CountryInfoResponse & { entitlements?: Array<string | EntitlementType> }
        )
      );

      setTop10CountriesInfo(normalizedRows);

      let countries = uniq(countriesInfo.map((country) => country.country));
      if (!formMetadata.isNew && collection?.countries) {
        countries = uniq([...countries, ...collection.countries]);
      }
      setAvailableTop10Countries(countries);
    };

    fetchTop10CountriesInfo();
  }, [isTop10, collection, formMetadata.isNew, collectionEntityId]);

  useEffect(() => {
    reset(collection);
    setIsForSimilarsDisabled(!collection?.genre?.key);

    if (!isVOD) {
      setValue('collectionType', getContentCollectionType());
      setValue('forSimilars', false);

      if (isTop10) {
        setValue('sortingType', ContentCollectionSortingType.CURATED_FIRST);
      }
    }

    initTop10CountriesFields();
  }, [collection, initTop10CountriesFields]);

  useEffect(() => {
    return () => {
      top10CountriesFetchGenerationRef.current += 1;
    };
  }, []);

  const onSubmit = async (collection: ContentCollectionResponse, shouldPublish = false) => {
    if (!(await isFormValid(formRef))) return;
    if (collection?.genre?.key === '') {
      collection = omit(collection, 'genre');
    }

    if (
      !collection?.autoRotate?.numberOfItems &&
      !collection?.autoRotate?.numberOfRotationDays &&
      !collection?.autoRotate?.startDate
    ) {
      collection = omit(collection, 'autoRotate');
    }

    if (collection?.autoRotate && !collection?.autoRotate?.startDate) {
      collection = {
        ...collection,
        autoRotate: {
          ...collection.autoRotate,
          startDate: DateTime.now().toISO()
        }
      };
    }

    let savedCollection: ContentCollectionResponse | undefined;

    if (shouldPublish) {
      if (hasUpsertPermission) {
        // save and publish
        savedCollection = await saveAndPublish(collection);
      } else {
        savedCollection = await publish(collection.entityId);
      }
    } else {
      // save as draft
      savedCollection = await save(collection);
    }

    if (!savedCollection) return;
    if (formMetadata.isNew) {
      navigate(getAppRoute(savedCollection.entityId));
    }
    closeForm();
  };
  const onError: SubmitErrorHandler<ContentCollectionResponse> = async (errors) => {
    if (errors.splashLandscapeImage || errors.splashPortraitImage) {
      notifyError(t('errors.collections.splash_images'));
      return;
    }

    notifyError(t('general.form_error'));
  };

  const handleDeleteConfirm = async () => {
    if (!collection) return;

    const result = await confirm({
      confirmColor: 'error',
      confirmText: t('confirm.confirm_delete_button'),
      body: <CollectionDeleteConfirm collection={collection} />,
      'data-testid': collectionFormTestIds.confirmDialog
    });

    if (result) {
      const successful = await remove((collection as ContentCollectionResponse).entityId);
      if (successful) {
        closeForm();
        navigate(getAppRoute());
      }
    }
  };

  const handleOnClose = () => {
    if (!isSaving && !isPublishing) {
      closeForm();
    }
  };

  const renderCollectionForm = () => (
    <Drawer
      data-testid={collectionFormTestIds.root}
      open={formMetadata.isShowingForm}
      formRef={formRef}
      onClose={handleOnClose}
      headerLeft={
        <div className={classes.headerLeft} data-testid={collectionFormTestIds.headerLeft}>
          <Typography variant="h6">{getCollectionFormCopy(formMetadata.isNew)}</Typography>
          {(collection as ContentCollectionResponse)?.status === VersionedDocumentStatus.DRAFT &&
            !formMetadata.isNew && <DraftBadge />}
          {formMetadata.isCloning && cloningCollectionTitle && (
            <Chip
              size="small"
              color="secondary"
              label={
                <>
                  {t('general.clone_of')} <Localized prop={cloningCollectionTitle} />
                </>
              }
              data-testid={collectionFormTestIds.cloneBadge}
            />
          )}
        </div>
      }
      headerRight={
        collection ? (
          <Button
            color="error"
            endIcon={<Delete />}
            onClick={handleDeleteConfirm}
            disabled={!canDelete || formMetadata.isNew || collection.locked || !hasDeletePermission}
            data-testid={collectionFormTestIds.deleteButton}
            sx={{ ml: 4 }}
          >
            {t('general.delete')}
          </Button>
        ) : null
      }
      footerLeft={
        <>
          <Button
            onClick={handleSaveDraft(handleSubmit, onSubmit, onError)}
            color="secondary"
            loading={isSaving}
            disabled={!canSave || !hasUpsertPermission || isPublishing || isSaving}
            className={classes.footerButton}
            data-testid={collectionFormTestIds.saveButton}
          >
            {t('general.saveAsDraft')}
          </Button>
          {(!isTop10 || (isTop10 && !formMetadata.isNew)) && (
            <Button
              onClick={handleSavePublish(handleSubmit, onSubmit, onError)}
              loading={isPublishing}
              disabled={!canSave || !canPublish || !hasPublishPermission || isSaving || isPublishing}
              className={classes.footerButton}
              data-testid={collectionFormTestIds.publishButton}
            >
              {t(hasUpsertPermission ? 'general.saveAndPublish' : 'general.publish')}
            </Button>
          )}
        </>
      }
      footerRight={
        <Button
          color="grey"
          disabled={isSaving || isPublishing}
          className={classes.footerButton}
          onClick={closeForm}
          data-testid={collectionFormTestIds.cancelButton}
        >
          {t('general.cancel')}
        </Button>
      }
    >
      {collection && (
        <div className={classes.formBody} data-testid={collectionFormTestIds.formBody}>
          <Code obj={getValues()} />
          <LocalizedInputCollection
            containerClassName={classes.licContainer}
            reset={reset}
            getValues={getValues}
            fields={[
              {
                component: (
                  <InputController
                    name="title"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <TextValidator
                        fullWidth
                        name="title"
                        color={formControlColor}
                        value={value}
                        onChange={onChange}
                        label={t('general.title')}
                        validators={[CustomValidators.requiredIfDefined]}
                        errorMessages={[t('general.field_is_required')]}
                      />
                    )}
                  />
                )
              },
              {
                component: (
                  <InputController
                    name="description"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <TextValidator
                        fullWidth
                        name="description"
                        color={formControlColor}
                        value={value}
                        onChange={onChange}
                        label={t('general.description')}
                        validators={[CustomValidators.requiredIfDefined]}
                        errorMessages={[t('general.field_is_required')]}
                      />
                    )}
                  />
                )
              }
            ]}
          />

          <Controller
            name="editorNotes"
            control={control}
            render={({ field: { onChange, value } }) => (
              <TextValidator
                fullWidth
                multiline
                color={formControlColor}
                name="editorNotes"
                label={t('general.editor_notes')}
                value={value}
                onChange={onChange}
                data-testid={collectionFormTestIds.editorNotes}
              />
            )}
          />

          <div className={classes.metaGrid}>
            <Controller
              control={control}
              name="splashLandscapeImage"
              rules={{ required: splashPortraitImage }}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <AssetBrowser
                  label={t('collections.landscape_splash')}
                  value={value}
                  onChange={onChange}
                  assetType={AssetTypes.collections.landscapeSplash}
                  hasError={!!error}
                  data-testid={collectionFormTestIds.splashLandscape}
                />
              )}
            />
            <Controller
              control={control}
              name="splashPortraitImage"
              rules={{ required: splashLandscapeImage }}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <AssetBrowser
                  label={t('collections.portrait_splash')}
                  value={value}
                  onChange={onChange}
                  assetType={AssetTypes.collections.portraitSplash}
                  hasError={!!error}
                  data-testid={collectionFormTestIds.splashPortrait}
                />
              )}
            />
            <Controller
              control={control}
              name="cardLandscapeImage"
              render={({ field: { value, onChange } }) => (
                <AssetBrowser
                  label={t('collections.card_image_landscape')}
                  value={value}
                  onChange={onChange}
                  assetType={AssetTypes.collections.cardImageLandsape}
                  data-testid={collectionFormTestIds.cardImageLandscape}
                />
              )}
            />
            <Controller
              control={control}
              name="slug"
              render={({ field: { value, onChange } }) => (
                <Slug value={value} onChange={onChange} slugify={title?.[DocumentLocale.ES]} />
              )}
            />
          </div>

          <div className={classes.formField}>
            {collection.entitlements && (
              <Controller
                name="entitlements"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <EntitlementsMultiSelect
                    label={t('general.user_access_level')}
                    value={value as EntitlementType[]}
                    onChange={onChange}
                  />
                )}
              />
            )}
          </div>
          {isVOD && (
            <div className={classes.formField}>
              <FormControl className={classes.selectControl}>
                <InputLabel>{t('collections.collection_type')}</InputLabel>
                <Controller
                  name="collectionType"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Select
                      label={t('collections.collection_type')}
                      value={value || ''}
                      onChange={onChange}
                      SelectDisplayProps={{ style: { textTransform: 'capitalize' } }}
                      data-testid={collectionFormTestIds.contentTypeSelect}
                    >
                      {collectionTypes.map((collectionType) => (
                        <MenuItem
                          key={collectionType}
                          value={collectionType}
                          data-testid={''}
                          className={classes.selectOption}
                        >
                          {t(collectionTypeCaptions[collectionType as ContentCollectionType] as LocaleKeys)}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
            </div>
          )}
          {isTop10 && (
            <div className={classes.formField}>
              <Controller
                name="countries"
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, value, disabled }, fieldState: { error } }) => (
                  <CountryPickerLite
                    label={t('collections.top_10.countries')}
                    value={value?.filter((country) => !!country) || []}
                    onChange={onChange}
                    disableCountry={(country) => {
                      if (!availableTop10Countries.includes(country)) return true;
                      const selected = selectedEntitlements || [];
                      if (selected.length === 0) return false;
                      const publishedElsewhere = publishedTiersByCountry[country] ?? [];
                      return selected.some((ent) => publishedElsewhere.includes(ent));
                    }}
                    fullWidth
                    hidePresets
                    disabled={disabled}
                    error={error}
                  />
                )}
              />
            </div>
          )}
          {!isTop10 && (
            <div className={classes.formField}>
              <FormControl variant="standard" className={classes.selectControl}>
                <InputLabel>{t('general.genre')}</InputLabel>
                {genresList && (
                  <Controller
                    name="genre"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Select
                        label={t('general.genre')}
                        value={value?.key || ''}
                        onChange={({ target: { value } }) => {
                          setIsForSimilarsDisabled(!value);
                          if (!value) {
                            setValue('forSimilars', false);
                          }
                          onChange({ key: value });
                        }}
                        SelectDisplayProps={{ style: { textTransform: 'capitalize' } }}
                        data-testid={collectionFormTestIds.genresSelect}
                      >
                        <MenuItem value="">{t('general.select_a_genre')}</MenuItem>
                        {genresList.map((genre) => (
                          <MenuItem key={genre} value={genre} data-testid={''} className={classes.selectOption}>
                            {genre}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                )}
              </FormControl>
            </div>
          )}
          {isVOD && (
            <div className={classes.formField}>
              <Controller
                name="forSimilars"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <FormControlLabel
                    label={t('collections.for_similars')}
                    control={
                      <Checkbox
                        name="forSimilars"
                        data-testid={collectionFormTestIds.forSimilars}
                        checked={value}
                        color={formControlColor}
                        disabled={isForSimilarsDisabled}
                        onChange={({ target: { checked } }) => {
                          onChange(checked);
                        }}
                      />
                    }
                  />
                )}
              />
            </div>
          )}
          <div className={classes.formField}>
            <Controller
              name="kids"
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormControlLabel
                  label={t('collections.kids.enable')}
                  control={
                    <Checkbox
                      name="kids"
                      data-testid={collectionFormTestIds.kids}
                      checked={value}
                      color={formControlColor}
                      onChange={({ target: { checked } }) => {
                        onChange(checked);
                      }}
                    />
                  }
                />
              )}
            />
          </div>

          {!isTop10 && (
            <div className={classes.formField}>
              <FormControl className={classes.selectControl}>
                <InputLabel id="sortingType">{t('collections.sorting.sorting_type')}</InputLabel>
                <Controller
                  name="sortingType"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Select
                      id="sortingType"
                      label={t('collections.sorting.sorting_type')}
                      value={value || ContentCollectionSortingType.CURATED_FIRST}
                      onChange={onChange}
                      data-testid={collectionFormTestIds.sortingType}
                    >
                      {Object.keys(ContentCollectionSortingType).map((sortingType) => (
                        <MenuItem
                          className={classes.sortingMenuItem}
                          key={sortingType}
                          value={sortingType}
                          disabled={isMixed && sortingType === ContentCollectionSortingType.FALLBACK_CURATED}
                        >
                          <div>{t(sortingTypeCaptions[sortingType as ContentCollectionSortingType])}</div>
                          <div className={classes.sortingDescription}>
                            {t(sortingTypeDescriptions[sortingType as ContentCollectionSortingType])}
                          </div>
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
            </div>
          )}

          <div className={classes.formField}>
            <FormControl className={classes.selectControl}>
              <Controller
                name="ownerPermissionsGroups"
                rules={{ required: t('permissions.permissions_groups') }}
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <PermissionsGroupsSelector
                    error={!!error}
                    values={value || []}
                    onChange={onChange}
                    data-testid={collectionFormTestIds.ownerPermissionsGroupsSelect}
                  />
                )}
              />
            </FormControl>
          </div>

          <div>
            <Controller
              name="autoRotate"
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormControlLabel
                  label={t('collections.auto_rotate.enable')}
                  control={
                    <Checkbox
                      data-testid={collectionFormTestIds.autoRotateCheckbox}
                      checked={!!value}
                      color={formControlColor}
                      onChange={({ target: { checked } }) => {
                        if (checked) {
                          onChange(defaultAutoRotateParams());
                        } else {
                          onChange(null);
                        }
                      }}
                    />
                  }
                />
              )}
            />
          </div>
          {isAutoRotateEnabled && (
            <Stack direction="row" gap={6} alignItems="center">
              <Controller
                control={control}
                name="autoRotate.numberOfItems"
                render={({ field: { value, onChange } }) => (
                  <TextValidator
                    type="number"
                    name="autoRotate.numberOfItems"
                    value={value}
                    label={t('collections.auto_rotate.number_of_items')}
                    validators={['minNumber:1', 'matchRegexp:^[0-9]+$']}
                    color={formControlColor}
                    onChange={({ target }) => onChange(Number(target.value))}
                    data-testid={collectionFormTestIds.autoRotateNumItemsInput}
                  />
                )}
              />
              <Controller
                control={control}
                name="autoRotate.numberOfRotationDays"
                render={({ field: { value, onChange } }) => (
                  <TextValidator
                    type="number"
                    name="autoRotate.numberOfRotationDays"
                    value={value}
                    label={t('collections.auto_rotate.number_of_days')}
                    onChange={({ target }) => onChange(Number(target.value))}
                    validators={['minNumber:1', 'matchRegexp:^[0-9]+$']}
                    color={formControlColor}
                    data-testid={collectionFormTestIds.autoRotateNumDaysInput}
                  />
                )}
              />
              <Controller
                control={control}
                name="autoRotate.startDate"
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <div className={classes.startDate}>
                    {renderFormLabel(t('collections.auto_rotate.started_on'))}
                    <ClearableDateTimePicker
                      value={value}
                      onChange={onChange}
                      clearable={false}
                      fullWidth
                      showTimezones
                      error={!!error}
                      helperText={error ? error?.message : t('collections.auto_rotate.start_time_help_text')}
                    />
                  </div>
                )}
              />
            </Stack>
          )}
        </div>
      )}
    </Drawer>
  );

  return renderCollectionForm();
}

export default CollectionForm;
